import type {
  Intervention as SolverIntervention,
  InterventionSessionTypeEnum,
  SchedulingInput,
  SchedulingJobResultResponse,
  SchedulingResult,
  Staff,
  StaffOccupancy,
  StaffProfessionResponse
} from '../api/generated';
import { SatisfactionIssueCriterionGapEnum } from '../api/generated';
import type { SolverTimeWindow } from '../types/schedulingResultTypes';
import type {
  Intervention,
  PersonDayLoad,
  PlannedWeekData,
  SessionType,
  Skill,
  UnplannedWeekData,
  UnscheduledIntervention,
  WeekKpiSummary,
  WeekShell,
  Weekday
} from '../types/weekOverview';
import { SCHEDULED_WEEKDAYS, WEEKDAYS_ORDER } from '../constants/weekOverviewConstants';
import { getDateForWeekday, getIsoWeekStartDate } from '../utils/weekDateUtils';

const MS_PER_DAY = 86_400_000;
const MAX_UTILIZATION_PERCENT = 100;

// Plan id the solver's PatientLunchSynthesizer stamps on the lunch breaks it
// injects for every patient (LUNCH_PLAN_ID in the algorithm project). Those
// arrive as INTERNAL sessions but should render as lunch, not meetings.
const LUNCH_PLAN_ID = 'patient-lunch-break';

// The solver only distinguishes three session kinds. The richer UI palette
// (COURSE, ASSESSMENT, ...) has no counterpart in its output, so INTERNAL
// sessions render as meetings and the other two map one-to-one.
const SESSION_TYPE_BY_SOLVER_TYPE: Record<InterventionSessionTypeEnum, SessionType> = {
  INDIVIDUAL: 'INDIVIDUAL',
  GROUP: 'GROUP',
  INTERNAL: 'MEETING'
};

function sessionTypeFor(definition: SolverIntervention): SessionType | null {
  if (definition.planId === LUNCH_PLAN_ID) return 'LUNCH';
  return definition.sessionType ? SESSION_TYPE_BY_SOLVER_TYPE[definition.sessionType] : null;
}

function parseTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':');
  return Number(hours) * 60 + Number(minutes);
}

function toIsoDateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function roundToTenth(value: number): number {
  return Number(value.toFixed(1));
}

// A staff member's working window for one date. The generated TimeWindow type
// has the wrong field names for the solver payload (an OpenAPI schema-name
// collision — see SolverTimeWindow), so the runtime shape is cast here.
function workingWindowFor(
  workingHours: Record<string, object> | undefined,
  date: string
): SolverTimeWindow | undefined {
  return workingHours?.[date] as SolverTimeWindow | undefined;
}

function windowDurationHours(window: SolverTimeWindow): number {
  return (parseTimeToMinutes(window.endTime) - parseTimeToMinutes(window.startTime)) / 60;
}

// A staff member is occupied from preparation through post-processing; the
// solver omits the outer times when an intervention needs no prep/wrap-up.
function occupancyMinutes(occupancy: StaffOccupancy): number | null {
  const start = occupancy.preparationStart ?? occupancy.interventionStart;
  const end = occupancy.postProcessingEnd ?? occupancy.interventionEnd;
  if (!start || !end) return null;
  return parseTimeToMinutes(end) - parseTimeToMinutes(start);
}

function buildWeekShell(year: number, week: number, locationName: string): WeekShell {
  const weekStart = getIsoWeekStartDate(year, week);
  const weekEnd = new Date(weekStart.getTime() + 6 * MS_PER_DAY);
  return {
    weekNumber: week,
    year,
    weekStartDate: weekStart.toISOString(),
    weekEndDate: weekEnd.toISOString(),
    locationName,
    skills: [],
    staff: [],
    rooms: [],
    patients: []
  };
}

// A week without a completed calculation: the header renders from the shell
// and the page shows the empty state based on `isPlanned`.
export function buildUnplannedWeekData(
  year: number,
  week: number,
  locationName: string
): UnplannedWeekData {
  return { ...buildWeekShell(year, week, locationName), isPlanned: false };
}

// Every profession referenced by the roster or an intervention requirement,
// named via the staff-profession register. Falls back to the raw id so an
// unknown profession still renders instead of disappearing from the heatmap.
function buildSkills(input: SchedulingInput, professions: StaffProfessionResponse[]): Skill[] {
  const referencedIds = new Set<string>();
  for (const staff of input.staff ?? []) {
    for (const professionId of staff.staffProfessionIds ?? []) referencedIds.add(professionId);
  }
  for (const intervention of input.interventions ?? []) {
    for (const requirement of intervention.mandatoryStaffCompetenceRequirements ?? []) {
      for (const professionId of requirement.staffProfessionIds ?? []) {
        referencedIds.add(professionId);
      }
    }
    for (const requirement of intervention.flexStaffCompetenceRequirements ?? []) {
      if (requirement.staffProfessionId) referencedIds.add(requirement.staffProfessionId);
    }
  }

  const nameById = new Map(professions.map((profession) => [profession.id, profession.name]));
  return [...referencedIds]
    .map((id) => ({ id, name: nameById.get(id) ?? id }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function requiredSkillIdsFor(intervention: SolverIntervention): string[] {
  const skillIds = new Set<string>();
  for (const requirement of intervention.mandatoryStaffCompetenceRequirements ?? []) {
    for (const professionId of requirement.staffProfessionIds ?? []) skillIds.add(professionId);
  }
  for (const requirement of intervention.flexStaffCompetenceRequirements ?? []) {
    if (requirement.staffProfessionId) skillIds.add(requirement.staffProfessionId);
  }
  return [...skillIds];
}

// Joins each scheduled assignment with its intervention definition from the
// solver input. Assignments outside the displayed week, missing their core
// fields, or referencing an unknown intervention are skipped rather than
// rendered half-empty.
function buildInterventions(
  result: SchedulingResult,
  dayByDate: Map<string, Weekday>
): Intervention[] {
  const interventionById = new Map<string, SolverIntervention>();
  for (const definition of result.input?.interventions ?? []) {
    if (definition.id) interventionById.set(definition.id, definition);
  }

  const interventions: Intervention[] = [];
  for (const assignment of result.plan ?? []) {
    // Room is intentionally not required — internal sessions such as the
    // solver-injected lunch breaks are placed without one.
    const { plannedInterventionId, assignedDate, assignedStartTime } = assignment;
    if (!plannedInterventionId || !assignedDate || !assignedStartTime) continue;

    const definition = interventionById.get(plannedInterventionId);
    const day = dayByDate.get(assignedDate);
    if (!definition || !day) continue;

    const sessionType = sessionTypeFor(definition);
    if (!sessionType) continue;

    const startMinutes = parseTimeToMinutes(assignedStartTime);
    const staffIds = (assignment.staffOccupancies ?? [])
      .map((occupancy) => occupancy.staffId)
      .filter((staffId): staffId is string => Boolean(staffId));

    interventions.push({
      id: plannedInterventionId,
      title: definition.name ?? '',
      sessionType,
      programLabel: null,
      day,
      startMinutes,
      endMinutes: startMinutes + (definition.durationMinutes ?? 0),
      roomId: assignment.assignedRoomId ?? '',
      staffIds: [...new Set(staffIds)],
      patientIds: definition.patientIds ?? [],
      requiredSkillIds: requiredSkillIdsFor(definition),
      notes: null
    });
  }
  return interventions;
}

// Interventions the solver could not place, merged from its two reporting
// channels: diagnostics.unscheduledDiagnostics (with the feasible-slot count)
// and the UNSCHEDULED_COUNT satisfaction issues. Each id is joined with its
// definition from the input snapshot; an id without a definition still renders
// (title falls back to the raw id) rather than disappearing from the report.
function buildUnscheduledInterventions(result: SchedulingResult): UnscheduledIntervention[] {
  const feasibleCountById = new Map<string, number | null>();
  for (const diagnostic of result.diagnostics?.unscheduledDiagnostics ?? []) {
    if (!diagnostic.interventionId) continue;
    feasibleCountById.set(diagnostic.interventionId, diagnostic.feasibleCandidateCount ?? null);
  }

  const unscheduledIds = new Set(feasibleCountById.keys());
  for (const issue of result.issues ?? []) {
    if (issue.criterionGap !== SatisfactionIssueCriterionGapEnum.UnscheduledCount) continue;
    if (issue.interventionId) unscheduledIds.add(issue.interventionId);
  }

  const interventionById = new Map<string, SolverIntervention>();
  for (const definition of result.input?.interventions ?? []) {
    if (definition.id) interventionById.set(definition.id, definition);
  }

  return [...unscheduledIds]
    .map((id): UnscheduledIntervention => {
      const definition = interventionById.get(id);
      return {
        id,
        title: definition?.name ?? id,
        sessionType: definition ? sessionTypeFor(definition) : null,
        patientIds: definition?.patientIds ?? [],
        requiredSkillIds: definition ? requiredSkillIdsFor(definition) : [],
        durationMinutes: definition?.durationMinutes ?? null,
        feasibleCandidateCount: feasibleCountById.get(id) ?? null
      };
    })
    .sort((a, b) => a.title.localeCompare(b.title));
}

// The roster facts needed to score one staff member's week, resolved once per
// person so the per-day work stays a lookup.
type StaffRoster = {
  staffId: string;
  workingHours: Staff['workingHours'];
  holidayDates: Set<string>;
};

// Booked minutes per staff member per weekday, keyed `${staffId}|${day}`.
// Occupancies span preparation through post-processing, so a person can be
// booked beyond the intervention times themselves.
function buildBookedMinutesByStaffDay(
  result: SchedulingResult,
  dayByDate: Map<string, Weekday>
): Map<string, number> {
  const bookedMinutesByStaffDay = new Map<string, number>();

  for (const assignment of result.plan ?? []) {
    const day = assignment.assignedDate ? dayByDate.get(assignment.assignedDate) : undefined;
    if (!day) continue;
    for (const occupancy of assignment.staffOccupancies ?? []) {
      const minutes = occupancyMinutes(occupancy);
      if (!occupancy.staffId || minutes === null) continue;
      const key = `${occupancy.staffId}|${day}`;
      const booked = bookedMinutesByStaffDay.get(key) ?? 0;
      bookedMinutesByStaffDay.set(key, booked + minutes);
    }
  }

  return bookedMinutesByStaffDay;
}

// One person's load on one day, or null when the day should not appear at all:
// a non-scheduled weekday nobody works stays closed in the heatmap.
function buildStaffDayLoad(
  roster: StaffRoster,
  day: Weekday,
  date: string,
  bookedMinutes: number
): PersonDayLoad | null {
  const workingWindow = workingWindowFor(roster.workingHours, date);
  if (!workingWindow && !SCHEDULED_WEEKDAYS.includes(day)) return null;

  const isAbsent = !workingWindow || roster.holidayDates.has(date);
  const availableHours = isAbsent || !workingWindow ? 0 : windowDurationHours(workingWindow);
  const bookedHours = bookedMinutes / 60;
  const utilizationPercent =
    availableHours > 0
      ? Math.min(MAX_UTILIZATION_PERCENT, Math.round((bookedHours / availableHours) * 100))
      : 0;

  return {
    staffId: roster.staffId,
    day,
    bookedHours: roundToTenth(bookedHours),
    availableHours: roundToTenth(availableHours),
    utilizationPercent,
    isAbsent
  };
}

// One load entry per person per day: available hours from the roster's working
// window, booked hours from the person's scheduled occupancies (prep and
// post-processing included). Weekend days only appear when someone actually
// has working hours there, so the heatmap keeps rendering them as closed.
function buildPersonDayLoads(
  result: SchedulingResult,
  dayByDate: Map<string, Weekday>
): PersonDayLoad[] {
  const bookedMinutesByStaffDay = buildBookedMinutesByStaffDay(result, dayByDate);
  const dateByDay = new Map([...dayByDate].map(([date, day]) => [day, date]));
  const loads: PersonDayLoad[] = [];

  for (const staff of result.input?.staff ?? []) {
    if (!staff.id) continue;
    const roster: StaffRoster = {
      staffId: staff.id,
      workingHours: staff.workingHours,
      holidayDates: new Set(staff.holidayDates ?? [])
    };

    for (const day of WEEKDAYS_ORDER) {
      const date = dateByDay.get(day);
      if (!date) continue;

      const bookedMinutes = bookedMinutesByStaffDay.get(`${roster.staffId}|${day}`) ?? 0;
      const load = buildStaffDayLoad(roster, day, date, bookedMinutes);
      if (load) loads.push(load);
    }
  }

  return loads;
}

function buildKpis(
  input: SchedulingInput,
  interventions: Intervention[],
  loads: PersonDayLoad[]
): WeekKpiSummary {
  let interventionHours = 0;
  let otherBookingHours = 0;

  // Patient-facing sessions (individual + group) count as interventions;
  // everything else that is real work — meetings and other internal bookings —
  // counts as other bookings. Lunch breaks are neither work nor white time, so
  // they stay out of the task distribution entirely, mirroring how the occupancy
  // side ignores them.
  for (const intervention of interventions) {
    const hours = (intervention.endMinutes - intervention.startMinutes) / 60;
    if (intervention.sessionType === 'INDIVIDUAL' || intervention.sessionType === 'GROUP') {
      interventionHours += hours;
    } else if (intervention.sessionType !== 'LUNCH') {
      otherBookingHours += hours;
    }
  }

  let totalAvailable = 0;
  let totalBooked = 0;
  for (const load of loads) {
    totalAvailable += load.availableHours;
    totalBooked += load.bookedHours;
  }

  const patientCount = input.patients?.length ?? 0;

  return {
    occupancyBookedHours: Math.round(totalBooked),
    occupancyAvailableHours: Math.round(totalAvailable),
    taskDistribution: {
      interventionHours: Math.round(interventionHours),
      // Real staff-leave data is not part of the scheduling result yet
      // (arrives via REH-200); until then vacation stays empty rather than
      // guessing it from roster gaps, which would misreport part-time gaps.
      vacationHours: 0,
      otherBookingHours: Math.round(otherBookingHours),
      whiteTimeHours: Math.round(Math.max(0, totalAvailable - totalBooked))
    },
    staffCount: input.staff?.length ?? 0,
    interventionCount: interventions.filter(
      (i) => i.sessionType !== 'MEETING' && i.sessionType !== 'LUNCH'
    ).length,
    patientsAdmitted: patientCount,
    patientsTotal: patientCount
  };
}

// Maps the newest completed solver result onto the week overview's view model.
// The result embeds its full input snapshot (rooms, staff, patients,
// intervention definitions), so everything except profession names and the
// location name is self-contained.
export function mapSchedulingResultToWeekData(
  response: SchedulingJobResultResponse,
  year: number,
  week: number,
  locationName: string,
  professions: StaffProfessionResponse[]
): PlannedWeekData {
  const result = response.result;
  const input = result.input ?? {};

  // Date-key → weekday lookup for the displayed week; assignments on other
  // dates (defensive — the job is per-week) fall outside and are dropped.
  const dayByDate = new Map<string, Weekday>(
    WEEKDAYS_ORDER.map((day) => [toIsoDateKey(getDateForWeekday(year, week, day)), day])
  );

  const interventions = buildInterventions(result, dayByDate);
  const personDayLoads = buildPersonDayLoads(result, dayByDate);

  return {
    ...buildWeekShell(year, week, locationName),
    isPlanned: true,
    lastModifiedDate: response.finishedAt,
    skills: buildSkills(input, professions),
    staff: (input.staff ?? [])
      .map((staff) => ({
        id: staff.id ?? '',
        name: staff.displayName ?? '',
        skillIds: staff.staffProfessionIds ?? []
      }))
      .filter((staff) => staff.id !== '')
      .sort((a, b) => a.name.localeCompare(b.name)),
    rooms: (input.rooms ?? [])
      .map((room) => ({ id: room.id ?? '', name: room.displayName ?? '' }))
      .filter((room) => room.id !== ''),
    patients: (input.patients ?? [])
      .map((patient) => ({ id: patient.id ?? '', name: patient.displayName ?? '' }))
      .filter((patient) => patient.id !== ''),
    closedDays: [],
    personDayLoads,
    interventions,
    unscheduledInterventions: buildUnscheduledInterventions(result),
    kpis: buildKpis(input, interventions, personDayLoads)
  };
}
